/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useContext, useEffect, useRef, useState } from "react"
import styled, { ThemeContext, css } from "styled-components"
import SwiperCore, { Pagination, Navigation } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  AppFunctionComponent,
  HeroBuilder,
  RenderRichText,
} from "../../../types"
import "swiper/swiper-bundle.min.css"
import { ScrollAnchor } from "../sections/custom/innovation-lab-form/innovation-lab.component"
import HeroResolver from "../../../resolvers/hero/hero.resolver"
import { GatsbyImage } from "gatsby-plugin-image"
import { generateSources } from "../../../helpers/images"
import { resolveImages } from "../../../resolvers/responsive-image/responsive-image.resolver"
import { SectionContainer } from "../../../resolvers/text/text.resolver"
import {
  HeroHeading,
  HeroImage,
  HeroText,
} from "../sections/hero-section-refactored.component"
import RichText from "../../../resolvers/rich-text/rich-text.resolver"
import { SectionHeading, SectionVideo } from "../sections/section.component"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { SliderButtonStyle } from "../sections/custom/quick-start/quick-start.styled"
import useTranslationHook from "../../../hooks/use-translation.hook"

export const ArrowContainer = styled.div<{ active: boolean }>`
  top: 50%;
  opacity: 1;
  width: 42px;
  height: 42px;
  display: flex;
  cursor: pointer;
  position: absolute;
  border-radius: 50%;
  align-items: center;
  flex-direction: row;
  pointer-events: all;
  visibility: visible;
  user-selectable: none;
  justify-content: center;
  transform: translateY(-50%);
  background-color: ${({ theme }) => theme.color.arrowBackgroundDark};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  z-index: 1000;
  .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin: 0 4px;
    cursor: pointer;
    background-color: ${({ theme }) => theme.color.dot.inactive};
    opacity: 1;
    &:hover {
      background-color: ${({ theme }) => theme.color.dot.hover};
    }
  }

  .swiper-pagination-bullet-active {
    background-color: ${({ theme }) => theme.color.dot.active};
  }

  &.swiper-button-disabled {
    display: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    display: none;
  }

  ${({ active }) =>
    !active &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`

export const ArrowPrev = styled(ArrowContainer)`
  left: 5%;

  svg {
    transform: rotateZ(180deg);
  }
`

export const ArrowNext = styled(ArrowContainer)`
  right: 5%;
`

export const ControlsPrevIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M13.2929 6.05078L7.58594 11.7578L13.2929 17.4648L14.7069 16.0508L10.4139 11.7578L14.7069 7.46478L13.2929 6.05078Z"
      fill="black"
    />
  </svg>
)

export const ControlsNextIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M9.70697 17.707L15.414 12L9.70697 6.29297L8.29297 7.70697L12.586 12L8.29297 16.293L9.70697 17.707Z"
      fill="black"
    />
  </svg>
)

const SwiperWrapper = styled(Swiper)`
  width: 100%;
`

interface Props {
  slides: HeroBuilder[]
  anchorId?: string
  isArrowsDisabled?: boolean
  text?: RenderRichText
}

SwiperCore.use([Pagination, Navigation])

const TextWithEmbedCarousel: AppFunctionComponent<Props> = ({
  slides,
  text,
  anchorId,
}) => {
  const [swiper, setSwiper] = useState<SwiperCore>()
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const theme = useContext(ThemeContext)
  const { translate } = useTranslationHook()

  const filteredSlides = slides.filter(
    ({ entryName }) => entryName !== "Coming soon"
  )

  useEffect(() => {
    if (swiper) {
      setActiveIndex(swiper.activeIndex)
    }
  }, [swiper?.activeIndex])

  useEffect(() => {
    const updateSliderPosition = () => {
      const container = document.querySelector(".pagination-mobile")
      const activeThumbnail = container?.querySelector(".active-thumbnail")

      if (activeThumbnail && container) {
        const containerWidth = container.clientWidth
        const activeWidth = activeThumbnail.clientWidth
        //@ts-ignore
        const activeOffsetLeft = activeThumbnail.offsetLeft
        const offset = containerWidth / 2 - activeWidth / 2
        const translateX = activeOffsetLeft - offset
        if (activeIndex === 0) {
          //@ts-ignore
          container.style.transform = `translateX(-${0}px)`
        } else {
          //@ts-ignore
          container.style.transform = `translateX(-${translateX}px)`
        }
      }
    }

    updateSliderPosition()
  }, [activeIndex])

  return (
    <SliderContainer>
      {text && (
        <StyledSectionText>
          <SectionHeading>{renderRichText(text)}</SectionHeading>
        </StyledSectionText>
      )}
      <StyledThumbnailsWrapper className="thumbnails-wrapper">
        <StyledThumbnailsContainerMobile
          $activeIndex={activeIndex}
          className="pagination-mobile"
        >
          {slides.map(
            ({ image, headingText, id, entryName, thumbnailImage }, i) => {
              const isSlideAvailable = entryName !== "Coming soon"
              return (
                <StyledThumbnail
                  //@ts-ignore
                  onClick={() => {
                    swiper && isSlideAvailable && swiper.slideTo(i, 500)
                    const objectPosition = ref.current?.getBoundingClientRect()
                    if (window.innerWidth <= 800) {
                      window.scrollTo(
                        objectPosition!.left,
                        objectPosition!.top + window.scrollY - 180
                      )
                    } else {
                      window.scrollTo(
                        objectPosition!.left,
                        objectPosition!.top + window.scrollY - 60
                      )
                    }
                  }}
                  $active={activeIndex === i}
                  $disabled={!isSlideAvailable}
                  key={id + String(i)}
                  className={activeIndex === i ? "active-thumbnail" : ""}
                >
                  {image && (
                    <GatsbyImage
                      image={
                        thumbnailImage?.gatsbyImageData ||
                        generateSources(resolveImages(image, theme))
                      }
                      alt="id"
                    />
                  )}
                  <HeroHeading>
                    {headingText && <RichText content={headingText} />}
                  </HeroHeading>
                </StyledThumbnail>
              )
            }
          )}
        </StyledThumbnailsContainerMobile>
      </StyledThumbnailsWrapper>
      <StyledSwiperWrapper
        ref={ref}
        onSwiper={(swiper) => setSwiper(swiper)}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.activeIndex)
        }}
        navigation={{
          nextEl: ".swiper-arrow-right-embed",
          prevEl: ".swiper-arrow-left-embed",
        }}
        data-testid="hero-carousel"
      >
        <ScrollAnchor id={anchorId} />
        {filteredSlides.map((slide, index) => {
          const isActive = index === activeIndex
          const isNearby = Math.abs(index - activeIndex) <= 1

          if (isActive || isNearby) {
            return (
              <SwiperSlide key={index}>
                <HeroResolver hasDisclaimer isActive={isActive} {...slide} />
              </SwiperSlide>
            )
          } else {
            // Return an empty slide or placeholder for inactive slides
            return (
              <SwiperSlide key={index}>
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                ></div>
              </SwiperSlide>
            )
          }
        })}
      </StyledSwiperWrapper>
      <StyledThumbnailsWrapper>
        <StyledButtonsWrapper
          id="sneak-peaks"
          style={{ marginLeft: "0px", marginRight: "0px", width: "100%" }}
        >
          {/* <StyledExtraLink to={"#newsletter"} aria-label={"join"}>
            {translate("Join the Waitlist")}
          </StyledExtraLink> */}
          <StyledControlsButtonWrapper>
            <SliderButtonStyle
              className="swiper-arrow-left-embed"
              disabledPrev={activeIndex === 0}
              onClick={() => {
                const objectPosition = ref.current?.getBoundingClientRect()
                window.scrollTo(
                  objectPosition!.left,
                  objectPosition!.top + window.scrollY - 60
                )
              }}
            >
              <ControlsPrevIcon />
              {translate("PREV")}
            </SliderButtonStyle>
            <SliderButtonStyle
              className="swiper-arrow-right-embed"
              disabledNext={activeIndex === filteredSlides.length - 1}
              onClick={() => {
                const objectPosition = ref.current?.getBoundingClientRect()
                window.scrollTo(
                  objectPosition!.left,
                  objectPosition!.top + window.scrollY - 60
                )
              }}
            >
              {translate("NEXT")}
              <ControlsNextIcon />
            </SliderButtonStyle>
          </StyledControlsButtonWrapper>
        </StyledButtonsWrapper>
        <StyledThumbnailsContainer
          $activeIndex={activeIndex}
          className="pagination"
        >
          {slides.map(
            ({ image, headingText, id, entryName, thumbnailImage }, i) => {
              const isSlideAvailable = entryName !== "Coming soon"
              return (
                <StyledThumbnail
                  //@ts-ignore
                  onClick={() => {
                    swiper && isSlideAvailable && swiper.slideTo(i, 500)
                    const objectPosition = ref.current?.getBoundingClientRect()
                    if (window.innerWidth <= 800) {
                      window.scrollTo(
                        objectPosition!.left,
                        objectPosition!.top + window.scrollY - 180
                      )
                    } else {
                      window.scrollTo(
                        objectPosition!.left,
                        objectPosition!.top + window.scrollY - 60
                      )
                    }
                  }}
                  $active={activeIndex === i}
                  className={activeIndex === i ? "active-thumbnail" : ""}
                  $disabled={!isSlideAvailable}
                  key={id + String(i)}
                >
                  {image && (
                    <GatsbyImage
                      image={
                        thumbnailImage?.gatsbyImageData ||
                        generateSources(resolveImages(image, theme))
                      }
                      alt="id"
                    />
                  )}
                  <HeroHeading>
                    {headingText && <RichText content={headingText} />}
                  </HeroHeading>
                </StyledThumbnail>
              )
            }
          )}
        </StyledThumbnailsContainer>
      </StyledThumbnailsWrapper>
      <StyledDisclaimer>{translate("kompakt-disclaimer")}</StyledDisclaimer>
    </SliderContainer>
  )
}

export default TextWithEmbedCarousel

const SliderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    overflow: hidden;
  }
`

const StyledSwiperWrapper = styled(SwiperWrapper)`
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    max-height: 670px;
    margin-top: 2rem;
  }

  section {
    align-items: center;
    height: unset;
    max-height: unset;

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      grid-template-columns: 1fr 1fr;
    }
  }

  ${SectionVideo} {
    max-width: 570px;
    max-height: 570px;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      justify-self: end;
    }
  }
  ${HeroImage} {
    border-radius: 8px;
    border: 2px solid #cdcdcd;
    padding: 2rem;

    img {
      top: 0;
      width: 100%;
      height: 120%;
      object-fit: cover;
      object-position: top;
    }
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      img {
        top: 0;
        width: 100%;
        height: 120%;
        object-fit: contain;
        object-position: top;
      }
    }
  }
  ${HeroHeading} {
    padding-left: 0 !important;
    h1 {
      text-align: left;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      max-width: 570px;
    }
  }
  ${HeroText} {
    padding-left: 0 !important;
    p {
      text-align: left;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      max-width: 580px;
    }
  }
`

const StyledSectionText = styled(SectionContainer)`
  width: 100%;
  padding: 0 20px;
  ${SectionHeading} {
    h1 {
      color: #000;
      text-align: center;
      font-family: Facto;
      font-size: 46px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
    }
    p {
      color: #000;
      text-align: center;
      font-family: Montserrat;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
    }
    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      h1 {
        color: #000;
        font-family: Facto;
        font-size: 30px;
        font-style: normal;
        font-weight: 400;
        line-height: 120%;
        text-align: left;
      }
      p {
        color: #000;
        font-family: Montserrat;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
        text-align: left;
      }
    }
  }
`

const StyledThumbnailsWrapper = styled(SectionContainer)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: static;

  max-width: 1260px;
  width: calc(100% - 40px);

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    margin-top: 0;
  }
`
const StyledThumbnailsContainer = styled.div<{
  $activeIndex: number
}>`
  display: grid;
  grid-template-columns: repeat(4, 240px);
  grid-template-rows: auto;
  grid-row-gap: 15px;
  grid-column-gap: 50px;
  justify-content: space-between;
  position: relative;
  transition: transform 0.3s ease-in-out;
  overflow: visible;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: none;
  }
`

const StyledThumbnailsContainerMobile = styled(StyledThumbnailsContainer)`
  display: none;
  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: flex;
    gap: 30px;
    transition: transform 0.3s ease-in-out;
  }
`

const StyledThumbnail = styled.div<{ $active?: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  /* max-width: 250px; */
  width: 100%;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    min-width: 170px;
    width: max-content;
    max-width: 200px;
  }
  h1 {
    font-family: Facto;
    font-size: 20px;
    font-weight: 400;
    line-height: 26px;
    text-align: left;
    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      color: ${({ $disabled }) => ($disabled ? "#CCC" : "black")};
      font-family: Facto;
      font-size: 20px;
      font-style: normal;
      font-weight: ${({ $active }) => ($active ? "600" : "400")};
      line-height: 100%;
    }
  }

  .gatsby-image-wrapper {
    border-radius: 9px;
    width: 240px;
    height: 192px;
    border: 2px solid;
    border-color: transparent;
    ${({ $active }) =>
      $active &&
      css`
        border-color: #000;
      `}

    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      display: none;
    }
  }
  img {
    width: 100%;
    object-fit: contain;
    object-position: top;
    transition: all 2s ease-in-out;
  }
`

const StyledButtonsWrapper = styled(SectionContainer)`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, 240px);
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  margin-bottom: 2rem;
  max-width: unset;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    left: 0;
    gap: 5rem;
    margin-top: 0;
    display: none !important;
  }
`

const StyledControlsButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  height: 100%;
  grid-column: 3/5;
  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: none;
  }

  button {
    cursor: pointer;
    min-width: 240px;

    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      min-width: unset;
    }
  }
`
// const StyledExtraLink = styled(BasicButtonLink)`
//   min-width: 236px;

//   @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
//     min-width: 190px;
//     margin-top: 0;
//     width: 196px;
//   }
// `

const StyledDisclaimer = styled.h3`
  font-family: Facto;
  font-size: 14px;
  font-weight: 400;
  line-height: 18.2px;
  text-align: left;
  color: #a0a0a0;

  width: 100%;
  max-width: 1260px;
  margin: auto;
  margin-top: 50px;
  margin-bottom: 0;
  padding-left: 20px;
  padding-right: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: none;
  }
`
